import { FunctionComponent, useState, useEffect, useRef } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Button, Grid, MenuItem, Typography, List, ListItem, ListItemText, Paper, ClickAwayListener, Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { StyledSelect, StyledTextField } from '../../components/ui/formInput'
//import RecentOrderHistory from '../../components/common/recent-order-history'
import { useGetCompanyCustomerViewOrders, useGetCompanyOpenItems, useGetOrderStatusSoAutocomplete, useGetOrderStatusPoAutocomplete } from '../../hooks/api/oms';
import { useFindInvoice, useGetInvoiceAutocomplete } from '../../hooks/api/invoice';
import { useSelector } from 'react-redux';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  statusOrderNumber: ValidateField;
  purchaseOrderNumber: ValidateField;
  customerOrder: ValidateField;
  openItem: ValidateField;
};

const CheckOrderStatus: FunctionComponent = () => {
  const navigate = useNavigate();
  const [statusOrderNumber, setStatusOrderNumber] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [ customerOrder, setCustomerOrder ] = useState('');
  const [openItem, setOpenItem] = useState('');
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState<boolean>(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceSuccess, setInvoiceSuccess] = useState<boolean>(false);
  const { mutateAsync: findInvoice } = useFindInvoice();
  const detailedPermissions = useSelector((state: any) => state.permission.detailedPermissions);
  const canSearchInvoice = detailedPermissions?.oms?.invoiceSearch;
  const userInfo = useSelector((state: any) => state.auth.user);
  const isCPUser = userInfo?.accessLevel === 'CP';

  // Autocomplete states
  const [showSoAutocomplete, setShowSoAutocomplete] = useState<boolean>(false);
  const [showPoAutocomplete, setShowPoAutocomplete] = useState<boolean>(false);
  const [showInvoiceAutocomplete, setShowInvoiceAutocomplete] = useState<boolean>(false);
  const [debouncedSoPrefix, setDebouncedSoPrefix] = useState<string>('');
  const [debouncedPoPrefix, setDebouncedPoPrefix] = useState<string>('');
  const [debouncedInvoicePrefix, setDebouncedInvoicePrefix] = useState<string>('');
  const soAutocompleteRef = useRef<HTMLDivElement>(null);
  const poAutocompleteRef = useRef<HTMLDivElement>(null);
  const invoiceAutocompleteRef = useRef<HTMLDivElement>(null);

  const {data: customersForViewOrders} = useGetCompanyCustomerViewOrders();

  // Autocomplete queries
  const { data: soAutocompleteData, isLoading: isSoAutocompleteLoading } = useGetOrderStatusSoAutocomplete(debouncedSoPrefix);
  const { data: poAutocompleteData, isLoading: isPoAutocompleteLoading } = useGetOrderStatusPoAutocomplete(debouncedPoPrefix);
  const { data: invoiceAutocompleteData, isLoading: isInvoiceAutocompleteLoading } = useGetInvoiceAutocomplete(debouncedInvoicePrefix);
  const soSuggestions = soAutocompleteData?.soNumbers || [];
  const poSuggestions = poAutocompleteData?.poNumbers || [];
  const invoiceSuggestions = invoiceAutocompleteData?.invoiceNumbers || [];

  const formValidation: ValidateFieldsState & { invoiceNumber: ValidateField } = {
    statusOrderNumber: { invalid: false, error: '' },
    purchaseOrderNumber: { invalid: false, error: '' },
    customerOrder: { invalid: false, error: '' },
    openItem: { invalid: false, error: '' },
    invoiceNumber: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState(formValidation);

  // Debounce effects for autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      if (statusOrderNumber.trim().length >= 2) {
        setDebouncedSoPrefix(statusOrderNumber.trim());
        setShowSoAutocomplete(true);
      } else {
        setShowSoAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [statusOrderNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (purchaseOrderNumber.trim().length >= 2) {
        setDebouncedPoPrefix(purchaseOrderNumber.trim());
        setShowPoAutocomplete(true);
      } else {
        setShowPoAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [purchaseOrderNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (invoiceNumber.trim().length >= 2) {
        setDebouncedInvoicePrefix(invoiceNumber.trim());
        setShowInvoiceAutocomplete(true);
      } else {
        setShowInvoiceAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [invoiceNumber]);

  // Handle SO suggestion selection - auto-search
  const handleSoSuggestionClick = (suggestion: string) => {
    setStatusOrderNumber(suggestion);
    setShowSoAutocomplete(false);
    // Auto-trigger search
    setTimeout(() => {
      navigate('/order-management-system/check-order-status/so/' + suggestion);
    }, 100);
  };

  // Handle PO suggestion selection - auto-search
  const handlePoSuggestionClick = (suggestion: string) => {
    setPurchaseOrderNumber(suggestion);
    setShowPoAutocomplete(false);
    // Auto-trigger search
    setTimeout(() => {
      navigate('/order-management-system/check-order-status/po/' + suggestion);
    }, 100);
  };

  // Handle Invoice suggestion selection - auto-search
  const handleInvoiceSuggestionClick = (suggestion: string) => {
    setInvoiceNumber(suggestion);
    setShowInvoiceAutocomplete(false);
    // Auto-trigger invoice search
    setTimeout(() => {
      handleInvoiceSearch(suggestion);
    }, 100);
  };

  // Handle invoice search
  const handleInvoiceSearch = async (invoiceNum: string) => {
    try {
      const result = await findInvoice({ partNo: invoiceNum });
             if (result?.status === 200) {
         const blob = new Blob([result.data], { type: 'application/pdf' });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${invoiceNum}.pdf`;
         document.body.appendChild(link);
         link.click();
         link.remove();
         window.URL.revokeObjectURL(url);
         
         // Show success message
         setInvoiceSuccess(true);
         setInvoiceError(''); // Clear any previous errors
         setTimeout(() => {
           setInvoiceSuccess(false);
         }, 5000); // Hide after 5 seconds
       } else {
         setInvoiceError(result?.data?.message || 'An error occurred. Please try again.');
         setInvoiceSuccess(false); // Hide success message if there's an error
       }
         } catch (err: any) {
       const blob = err?.response?.data;
       if (blob && blob instanceof Blob && blob.type === 'application/json') {
         try {
           const text = await blob.text();
           const json = JSON.parse(text);
           const message = `${json.message ?? ''}${json.error ? `: ${json.error}` : ''}`;
           setInvoiceError(message || 'An unexpected error occurred.');
         } catch (e) {
           setInvoiceError('Failed to read error response.');
         }
       } else if (err?.response?.data?.message || err?.response?.data?.error) {
         setInvoiceError(`${err.response.data.message ?? ''}${err.response.data.error ? `: ${err.error}` : ''}`);
       } else if (err?.message) {
         setInvoiceError(err.message);
       } else {
         setInvoiceError('An unexpected error occurred.');
       }
       setInvoiceSuccess(false); // Hide success message on error
     }
  };

  // Handle input changes
  const handleStatusOrderNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStatusOrderNumber(value);
    setValidateFields(formValidation); // Clear validation errors
  };

  const handlePurchaseOrderNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPurchaseOrderNumber(value);
    // Immediately trigger search if value is pasted and length >= 2
    if (value.trim().length >= 2) {
      setDebouncedPoPrefix(value.trim());
      setShowPoAutocomplete(true);
    } else {
      setShowPoAutocomplete(false);
    }
    setValidateFields(formValidation); // Clear validation errors
  };

  const handleInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInvoiceNumber(value);
    setValidateFields(formValidation); // Clear validation errors
    setInvoiceSuccess(false); // Clear success message when input changes
    setInvoiceError(''); // Clear error message when input changes
  };

  // Handle click away from autocomplete
  const handleSoClickAway = () => {
    setShowSoAutocomplete(false);
  };

  const handlePoClickAway = () => {
    setShowPoAutocomplete(false);
  };

  const handleInvoiceClickAway = () => {
    setShowInvoiceAutocomplete(false);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    const hasStatusOrderNumber = !!statusOrderNumber?.trim();
    const hasPurchaseOrderNumber = !!purchaseOrderNumber?.trim();
    const hasCustomerOrder = !!customerOrder?.toString().trim();
    const hasOpenItem = !!openItem?.trim();
    const hasInvoiceNumber = !!invoiceNumber?.trim();

    const filledFields = [
      { key: 'statusOrderNumber', hasValue: hasStatusOrderNumber },
      { key: 'purchaseOrderNumber', hasValue: hasPurchaseOrderNumber },
      { key: 'customerOrder', hasValue: hasCustomerOrder },
      { key: 'openItem', hasValue: hasOpenItem },
      ...(canSearchInvoice ? [{ key: 'invoiceNumber', hasValue: hasInvoiceNumber }] : []),
    ];

    const filledCount = filledFields.filter(f => f.hasValue).length;
    filledFields.forEach(field => {
      errors[field.key as keyof typeof errors] = {
        invalid: false,
        error: '',
      };
    });
    if (filledCount === 0) {
      const errorMessage = 'Please enter one value';
      filledFields.forEach(field => {
        errors[field.key as keyof typeof errors] = {
          invalid: true,
          error: errorMessage,
        };
      });
      valid = false;
    }

    if (!valid) {
      setValidateFields(errors);
      return valid;
    }

    if (hasStatusOrderNumber && hasPurchaseOrderNumber && filledCount === 2) {
      const errorMessage = 'Please enter either Sales Order Number or Purchase Order Number (not both)';
      errors.statusOrderNumber = { invalid: true, error: errorMessage };
      errors.purchaseOrderNumber = { invalid: true, error: errorMessage };
      valid = false;
    } else {
      errors.statusOrderNumber = { invalid: false, error: '' };
      errors.purchaseOrderNumber = { invalid: false, error: '' };
    }

    if (!valid) {
      setValidateFields(errors);
      return valid;
    }

    const errorMessage = `Please fill exactly one of the ${canSearchInvoice ? 'five' : 'four'} fields`;

    if (filledCount !== 1) {
      filledFields.forEach(field => {
        const showError = filledCount === 0 || field.hasValue;
        errors[field.key as keyof typeof errors] = {
          invalid: showError,
          error: showError ? errorMessage : '',
        };
      });
      valid = false;
    } else {
      filledFields.forEach(field => {
        errors[field.key as keyof typeof errors] = {
          invalid: false,
          error: '',
        };
      });
    }

    setValidateFields(errors);
    return valid;
  };

  const handleViewOpenItems = () => {
    // For CP users, navigate to open items using their company info
    const customerNumber = userInfo?.company || userInfo?.custNum || '';
    navigate('/order-management-system/check-order-status/item/' + customerNumber);
  };

     const handleNext = async (e?: React.FormEvent<HTMLFormElement>) => {
     if (e) e.preventDefault();
     setValidateFields(formValidation);
     setInvoiceError('');
     setInvoiceSuccess(false); // Clear success message on new form submission
     if (!validateForm()) {
       return;
     }
    setError('');
    setDisabled(true);
    if (statusOrderNumber ) {
      navigate('/order-management-system/check-order-status/so/' + statusOrderNumber);
      setDisabled(false);
      return;
    }
    if (purchaseOrderNumber) {
      navigate('/order-management-system/check-order-status/po/' + purchaseOrderNumber);
      setDisabled(false);
      return;
    }
    if (customerOrder) {
      navigate('/order-management-system/check-order-status/customer/' + customerOrder);
      setDisabled(false);
      return;
    }
    if (openItem) {
      navigate('/order-management-system/check-order-status/item/' + openItem);
      setDisabled(false);
      return;
    }
    if (canSearchInvoice && invoiceNumber) {
      await handleInvoiceSearch(invoiceNumber);
      setDisabled(false);
      return;
    }
  };

  return (
          <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'} containerTitle={"Check Order Status"}>
      <form onSubmit={handleNext} >
      <Grid container spacing={2}>
        <Grid size={4}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Barksdale Sales Order Online
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <StyledTextField
              label="Enter Sales Order"
              value={statusOrderNumber}
              onChange={handleStatusOrderNumberChange}
              error={validateFields.statusOrderNumber.invalid}
              helperText={validateFields.statusOrderNumber.error}
            />
            {showSoAutocomplete && (
              <ClickAwayListener onClickAway={handleSoClickAway}>
                <Paper
                  ref={soAutocompleteRef}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: 200,
                    overflow: 'auto',
                    boxShadow: 3,
                    border: '1px solid #ddd',
                    mt: 0.5
                  }}
                >
                  <List dense>
                    {isSoAutocompleteLoading ? (
                      <ListItem>
                        <ListItemText primary="Loading..." />
                      </ListItem>
                    ) : soSuggestions.length > 0 ? (
                      soSuggestions.map((suggestion: string, index: number) => (
                        <ListItem
                          key={index}
                          component="div"
                          onClick={() => handleSoSuggestionClick(suggestion)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <ListItemText primary={suggestion} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary="No suggestions found" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </ClickAwayListener>
            )}
          </Box>
        </Grid>
        <Grid size={1} sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '2em',
          width: '20px',
        }}>
          Or
        </Grid>
        <Grid size={4}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Barksdale Purchase Order Number
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <StyledTextField
              label="Enter Purchase Order"
              value={purchaseOrderNumber}
              onChange={handlePurchaseOrderNumberChange}
              error={validateFields.purchaseOrderNumber.invalid}
              helperText={validateFields.purchaseOrderNumber.error}
            />
            {showPoAutocomplete && (
              <ClickAwayListener onClickAway={handlePoClickAway}>
                <Paper
                  ref={poAutocompleteRef}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: 200,
                    overflow: 'auto',
                    boxShadow: 3,
                    border: '1px solid #ddd',
                    mt: 0.5
                  }}
                >
                  <List dense>
                    {isPoAutocompleteLoading ? (
                      <ListItem>
                        <ListItemText primary="Loading..." />
                      </ListItem>
                    ) : poSuggestions.length > 0 ? (
                      poSuggestions.map((suggestion: string, index: number) => (
                        <ListItem
                          key={index}
                          component="div"
                          onClick={() => handlePoSuggestionClick(suggestion)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <ListItemText primary={suggestion} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary="No suggestions found" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </ClickAwayListener>
            )}
          </Box>
        </Grid>
        <Grid size={4}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            View Customer Orders (If Applicable)
          </Typography>
          <StyledSelect
            label="Select Customer"
            value={customerOrder}
            onChange={(e: any) => setCustomerOrder(e.target.value)}
            error={validateFields.customerOrder.invalid}
            helperText={validateFields.customerOrder.error}
          >
            {
              customersForViewOrders?.map((customer: any) => (
                <MenuItem key={customer.custNum} value={customer.custNum}>
                  {customer.companyName}
                </MenuItem>
              ))
            }
          </StyledSelect>
        </Grid>
        <Grid size={1} sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '2em',
          width: '20px',
        }}></Grid>
        <Grid size={4}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            View List of Your Open Orders
          </Typography>
          {isCPUser ? (
            <Button
              variant="contained"
              onClick={handleViewOpenItems}
              sx={{ 
                backgroundColor: '#c3183c', 
                padding: '6px 25px !important',
                minWidth: '120px',
                height: '42px'
              }}
            >
              View
            </Button>
          ) : (
            <StyledTextField
              label="Customer Account"
              value={openItem}
              onChange={(e: any) => setOpenItem(e.target.value)}
              error={validateFields.openItem.invalid}
              helperText={validateFields.openItem.error}
            />
          )}
        </Grid>
        {canSearchInvoice && (
          <Grid size={4}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Barksdale Invoice Number
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <StyledTextField
                label="Enter Invoice Number"
                value={invoiceNumber}
                onChange={handleInvoiceNumberChange}
                error={validateFields.invoiceNumber.invalid}
                helperText={validateFields.invoiceNumber.error}
              />
              {showInvoiceAutocomplete && (
                <ClickAwayListener onClickAway={handleInvoiceClickAway}>
                  <Paper
                    ref={invoiceAutocompleteRef}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 200,
                      overflow: 'auto',
                      boxShadow: 3,
                      border: '1px solid #ddd',
                      mt: 0.5
                    }}
                  >
                    <List dense>
                      {isInvoiceAutocompleteLoading ? (
                        <ListItem>
                          <ListItemText primary="Loading..." />
                        </ListItem>
                      ) : invoiceSuggestions.length > 0 ? (
                        invoiceSuggestions.map((suggestion: string, index: number) => (
                          <ListItem
                            key={index}
                            component="div"
                            onClick={() => handleInvoiceSuggestionClick(suggestion)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          >
                            <ListItemText primary={suggestion} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="No suggestions found" />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </ClickAwayListener>
              )}
            </Box>
          </Grid>
        )}
                 <Grid size={12}>
           {error && (
             <Alert severity="error" sx={{ mt: 2 }}>
               {error}
             </Alert>
           )}
           {canSearchInvoice && invoiceError && (
             <Alert severity="error" sx={{ mt: 2 }}>
               {invoiceError}
             </Alert>
           )}
           {canSearchInvoice && invoiceSuccess && (
             <Alert severity="success" sx={{ mt: 2 }}>
               Invoice downloaded successfully!
             </Alert>
           )}
         </Grid>
        <Grid size={12}>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} type="submit">
            SEARCH
          </Button>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} type="button" onClick={() => navigate(-1)}>
            BACK
          </Button>
        </Grid>
      </Grid>
      </form>
      {/* <RecentOrderHistory /> */}
    </DashboardLayout>
  )
}

export default CheckOrderStatus